import type { SupabaseClient } from '../../lib/supabase';
import type { MyDaySectionRow } from './myDayLens';

/**
 * The person's own My Day sections.
 *
 * Nothing here filters by user_id, and that is deliberate rather than an
 * oversight: every policy on my_day_sections is `user_id = auth.uid()`, so the
 * database returns one person's rows. A client-side `.eq('user_id', me)` would
 * read as the thing providing the isolation, and the day somebody removed it
 * nothing would look wrong.
 *
 * user_id IS set on insert, because the policy's WITH CHECK demands it match
 * the caller — a row without it is refused rather than silently misfiled.
 */

// One literal, unwrapped (PostgREST type inference).
// prettier-ignore
const SECTION_COLUMNS = 'id, name, position, created_at';

function fail(message: string): never {
  throw new Error(message);
}

export const myDaySectionKeys = {
  all: ['my-day-sections'] as const,
};

export async function fetchMyDaySections(client: SupabaseClient): Promise<MyDaySectionRow[]> {
  const { data, error } = await client.from('my_day_sections').select(SECTION_COLUMNS);
  if (error) {
    fail(error.message);
  }
  // Ordered in the lens, not here. sortSections is what the tests drive and
  // what the page draws from, so ordering in two places would let them drift.
  return data ?? [];
}

export async function createMyDaySection(
  client: SupabaseClient,
  input: { userId: string; name: string; position: number },
): Promise<MyDaySectionRow> {
  const { data, error } = await client
    .from('my_day_sections')
    .insert({ user_id: input.userId, name: input.name, position: input.position })
    .select(SECTION_COLUMNS)
    .single();
  if (error) {
    fail(error.message);
  }
  return data;
}

export async function renameMyDaySection(
  client: SupabaseClient,
  input: { id: string; name: string },
): Promise<void> {
  const { error } = await client
    .from('my_day_sections')
    .update({ name: input.name })
    .eq('id', input.id);
  if (error) {
    fail(error.message);
  }
}

/**
 * Reordering, one row per changed position.
 *
 * Sent as separate updates rather than an upsert: an upsert would need every
 * column of every row, and the only thing that changed is an integer. Two
 * writes is the normal case, because a move swaps a neighbouring pair.
 */
export async function moveMyDaySections(
  client: SupabaseClient,
  moves: readonly { id: string; position: number }[],
): Promise<void> {
  for (const move of moves) {
    const { error } = await client
      .from('my_day_sections')
      .update({ position: move.position })
      .eq('id', move.id);
    if (error) {
      fail(error.message);
    }
  }
}

/**
 * Deleting a section. THE TASKS ARE NOT TOUCHED HERE, and that is the whole
 * design: tasks.my_day_section_id is ON DELETE SET NULL, so the database drops
 * their pointer in the same statement and they resolve to To Do Today on the
 * next read. Deleting tasks alongside would be a second, silent operation on
 * somebody's work; letting the foreign key do it means there is no code path
 * that could ever get it wrong.
 */
export async function deleteMyDaySection(client: SupabaseClient, id: string): Promise<void> {
  const { error } = await client.from('my_day_sections').delete().eq('id', id);
  if (error) {
    fail(error.message);
  }
}
